import React, { useEffect, useState } from "react";
import clsx from "clsx";
import "./PropertyInfo.scss";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Modal } from "@mui/material";
import toast from "react-hot-toast";
import { TemplatesApi } from "../../../../../utils/api/Templates";
import { PropertyApi } from "../../../../../utils/api/Property";
import { CommonEnums } from "../../../../../enums/common.enums";
import FaceBookTrueIcon from "../../../../../components/icons/FaceBookTrueIcon";
import InstagramTrueIcon from "../../../../../components/icons/InstagramTrueIcon";
import LinkedInIcon from "../../../../../components/icons/LinkedInIcon";
import YouTubeIcon from "../../../../../components/icons/YouTubeIcon";
import TikTokIcon from "../../../../../components/icons/TikTokIcon";
import PreviewCircleIcon from "../../../../../components/icons/PreviewCircleIcon";
import DownloadIcon from "../../../../../components/icons/DownloadIcon";
import BackArrowIcon from "../../../../../components/icons/BackArrowIcon";
import Loading from "../../../../loading/Loading";
import LeftArrowIcon from "../../../../../components/icons/LeftArrowIcon";
import RightArrowIcon from "../../../../../components/icons/RightArrowIcon";

interface PropertyStageProps {
  onStageSelect: (stageLabel: string, templateType: string) => void;
  onContinue: () => void;
}

interface TemplateItem {
  slug: string;
  label: string;
  template_type: string;
  isStatic?: boolean;
  status?: string;
  config?: any;
  allow?: boolean;
}

interface HardCodedStage {
  label: string;
  status: string;
  type: string;
}

const API_BASE_URL = process.env.REACT_APP_API_ENDPOINT;

const PropertyJourneyInfo: React.FC = () => {
  const navigate = useNavigate();
  const { propertyPostId } = useParams<{ propertyPostId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const subscriptionType =
    searchParams.get("subscription_type") || "property_marketing";
  const listingType = searchParams.get("listing_type") || "sale";
  const postType = searchParams.get("post_type") || "image_post";
  const propertyAddress =
    searchParams.get("property_address") ||
    "46/1 Citrus Avenue, Hornsby, NSW 2077";
  const [templates, setTemplates] = useState<TemplateItem[]>([]);
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [templateData, setTemplateData] = useState<any>(null);
  const [inputs, setInputs] = useState<{ [key: string]: any }>({});
  const [formValues, setFormValues] = useState<{ [key: string]: any }>({});
  const [stageVisible, setStageVisible] = useState(true);
  const inputOrder = ["sold_price", "punch_line"];

  const [expandedStage, setExpandedStage] = useState<string | null>(null);
  const [skippedStages, setSkippedStages] = useState<TemplateItem[]>([]);

  const [viewAllModalOpen, setViewAllModalOpen] = useState(false);

  const hardCodedStages = [
    {
      label: "Auction",
      status: "pending",
      text: "Design generation is pending",
      type: "auction",
    },
    {
      label: "Sold",
      status: "pending",
      text: "Design generation is pending",
      type: "sold",
    },
    {
      label: "For Sale",
      status: "completed",
      text: "Completed",
      type: "for_sale",
    },
    {
      label: "Open Home",
      status: "scheduled",
      text: "Open home is scheduled and pending for upload",
      type: "open_home",
    },
    {
      label: "Coming Soon",
      status: "completed",
      text: "Completed",
      type: "coming_soon",
    },
  ];

  const extraStages: TemplateItem[] = hardCodedStages.map((stage) => ({
    slug: stage.type,
    label: stage.label,
    template_type: stage.type,
    isStatic: true,
    status: stage.status,
  }));

  const allCombinedStages = [...extraStages, ...templates];

  const mockExpandedItems = [
    {
      id: 1,
      image:
        "https://res.cloudinary.com/dxnjxjtwh/image/upload/v1772794870/Frame_1261156603_urera9.png",
      orientation: "landscape",
      agent_socials: ["facebook", "instagram"],
      agency_socials: ["facebook", "linkedin"],
      scheduled_on: "Nov 30, 2024 10:00 AM",
      current_status: "Completed",
      posted_on: "Nov 30, 2024 02:00 PM",
      generated_on: "Nov 20, 2024 10:00 AM",
      last_updated: "Nov 30, 2024 10:00 AM",
    },
    {
      id: 2,
      image:
        "https://res.cloudinary.com/dxnjxjtwh/image/upload/v1772794813/image-post-1d4fd5a1-8947-11f0-af12-a989c10daa3f-1756959897594_2_fvbph6.png",
      orientation: "portrait",
      agent_socials: ["facebook", "instagram"],
      agency_socials: ["facebook", "linkedin"],
      scheduled_on: "Nov 30, 2024 10:00 AM",
      current_status: "Completed",
      posted_on: "Nov 30, 2024 02:00 PM",
      generated_on: "Nov 20, 2024 10:00 AM",
      last_updated: "Nov 30, 2024 10:00 AM",
    },
  ];

  const allowedSlugs = [
    "sold_listing_image_post_agent_marketing",
    "sold_listing_story_post_agent_marketing",
    "leased_image_post_agent_marketing",
    "leased_story_post_agent_marketing",
  ];

  useEffect(() => {
    const fetchTemplates = async () => {
      setLoading(true);
      try {
        const response = await TemplatesApi.getTemplateConfigs({
          subscription_type: subscriptionType,
          listing_type: listingType,
        });

        if (response?.success) {
          const rawTemplates =
            response?.data?.data?.[postType]?.templates || [];

          const filteredTemplates = rawTemplates.filter(
            (t: any) => t.design_type === "property_post" && t.allow === true,
          );

          const simplified = filteredTemplates.map((t: any) => ({
            slug: t.slug,
            label: t.label,
            template_type: t.template_type || t.slug,
            config: t.config,
            allow: t.allow,
          }));

          setTemplates(simplified);
        } else {
          setTemplates([]);
        }
      } catch (err) {
        console.error("Template fetch error:", err);
        setTemplates([]);
      } finally {
        setLoading(false);
      }
    };

    if (subscriptionType && listingType && postType) {
      fetchTemplates();
    }
  }, [subscriptionType, listingType, postType]);

  const fetchTemplate = async (slug: string) => {
    setIsLoading(true);
    try {
      const response = await TemplatesApi.getTemplateConfigs({
        subscription_type: subscriptionType,
        listing_type: listingType,
      });

      if (!response?.success || !response.data?.data) return;

      const postTypeBlock = response.data.data[postType];
      if (!postTypeBlock?.templates) return;

      const foundTemplate = postTypeBlock.templates.find(
        (tpl: any) => tpl.slug === slug && tpl.config?.design_template?._id,
      );

      if (!foundTemplate) return;

      const designTemplateId = foundTemplate.config.design_template._id;
      const res = await TemplatesApi.getDesignTemplateById(designTemplateId);

      if (res.success && res.data) {
        const apiInputs = res.data.data?.inputs || {};
        const defaultValues: { [key: string]: any } = {};
        let index = 0;

        Object.keys(apiInputs).forEach((key) => {
          const charLimit = apiInputs[key]?.characters_limit || 100;
          defaultValues[key] = {
            value: apiInputs[key]?.value || "",
            handler: apiInputs[key]?.handler || key,
            label: apiInputs[key]?.label || key,
            template_input_index: index,
            characters_limit: charLimit,
          };
          index += 1;
        });

        defaultValues["property_stage_title"] = {
          value: res.data.data?.template_type || "Property Stage",
          label: "Property Stage Title",
          template_input_index: 0,
          characters_limit: 200,
        };

        setInputs(apiInputs);
        setFormValues(defaultValues);

        setTemplateData({
          ...res.data,
          generatedImage: res.data?.data?.template_image_ref?.original_file_path
            ? `${process.env.REACT_APP_API_ENDPOINT}/${res.data.data.template_image_ref.original_file_path}`
            : res.data?.data?.template_image
              ? `${process.env.REACT_APP_API_ENDPOINT}/${res.data.data.template_image}`
              : null,
        });
      }
    } catch (err) {
      console.error("Failed to fetch property template:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const getStageTitleFromTemplateType = (templateType: string) => {
    let clean = templateType.replace(/(_agent_marketing|_listing|_post)/gi, "");
    const words = clean
      .split("_")
      .map((w) => w.toUpperCase().charAt(0) + w.slice(1));

    return words.join(" ");
  };

  useEffect(() => {
    const templateType = searchParams.get("template_type");
    const slug = searchParams.get("selected_slug");

    if (templateType && slug) {
      setStageVisible(false);
      setSelectedSlug(slug);

      if (allowedSlugs.includes(slug)) {
        // fetchTemplate(slug);
      } else {
        // onStageSelect("", templateType);
      }
    }
  }, [searchParams]);

  const handleSelect = async (template: TemplateItem & any) => {
    setSelectedSlug(template.slug);

    try {
      const defaultStageTitle =
        template.config?.design_template?.template_type ||
        getStageTitleFromTemplateType(template.template_type || template.slug);

      setFormValues((prev) => ({
        ...prev,
        property_stage_title: {
          value: defaultStageTitle,
          label: "Property Stage Title",
          template_input_index: 0,
          characters_limit: 200,
        },
      }));

      if (!allowedSlugs.includes(template.slug)) {
        setIsProcessing(true);
        setModalOpen(true);

        if (propertyPostId) {
          await PropertyApi.updatePropertyPost(propertyPostId, {
            template_type: template.template_type,
            finalize: true,
          });
        }

        setStageVisible(false);

        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set("template_type", template.template_type);
          next.set("selected_slug", template.slug);
          next.set("step", CommonEnums.propertyPost.post_configuration.key);
          return next;
        });

        // onStageSelect(template.label, template.template_type);
        setTemplateData(null);

        setIsProcessing(false);
        setModalOpen(false);

        return;
      }

      if (propertyPostId) {
        setIsProcessing(true);
        await PropertyApi.updatePropertyPost(propertyPostId, {
          template_type: template.template_type,
          finalize: false,
        });
        setIsProcessing(false);
      }

      await fetchTemplate(template.slug);
      setStageVisible(false);

      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("template_type", template.template_type);
        next.set("selected_slug", template.slug);
        return next;
      });

      // onStageSelect(template.label, template.template_type);
    } catch (err) {
      console.error("Failed to update template_type", err);
      toast.error("Something went wrong while selecting the stage.");
      setIsProcessing(false);
      setModalOpen(false);
    }
  };

  const handleUpdatedesign = async () => {
    if (!propertyPostId) {
      toast.error("Property Post ID is missing!");
      return;
    }

    const inputsPayload: any = {};
    Object.keys(formValues).forEach((key) => {
      const val = formValues[key];
      if (val?.value && (val.enabled ?? true)) {
        inputsPayload[key] = {
          handler: val.handler || key,
          label: val.label || key,
          value: val.value,
          template_input_index: val.template_input_index ?? 0,
          characters_limit: val.characters_limit ?? 0,
        };
      }
    });

    if (!selectedSlug) {
      toast.error("No template slug selected!");
      return;
    }

    const payload = {
      template_type: selectedSlug,
      // inputs: inputsPayload,
      finalize: true,
    };

    setModalOpen(true);

    try {
      const res = await PropertyApi.updatePropertyPost(propertyPostId, payload);
      if (res?.error) {
        toast.error(res.error || "Internal server error!");
        setModalOpen(false);
        return;
      }
      setModalOpen(false);
      setIsGenerating(true);
      const pollInterval = 3000;
      const pollDesignStatus = async () => {
        try {
          const check = await PropertyApi.getPropertyPostById(propertyPostId);
          if (check?.error) {
            setTimeout(pollDesignStatus, pollInterval);
            return;
          }
          const propertyData = check?.data?.data;
          if (!propertyData) {
            setTimeout(pollDesignStatus, pollInterval);
            return;
          }
          const imagePath =
            propertyData?.design_activity_ref?.design_task_id
              ?.output_image_path;
          if (!imagePath) {
            setTimeout(pollDesignStatus, pollInterval);
            return;
          }
          const assetUrl = `${API_BASE_URL}/${imagePath}`;
          setTemplateData((prev: any) => ({
            ...prev,
            generatedImage: assetUrl,
          }));
          setIsGenerating(false);
          toast.success("Design generated successfully!");
        } catch (err) {
          console.error("Polling error:", err);
          setTimeout(pollDesignStatus, pollInterval);
        }
      };
      pollDesignStatus();
    } catch (err) {
      console.error("Update design error:", err);
      toast.error("Something went wrong while updating the design!");
      setModalOpen(false);
      setIsGenerating(false);
    }
  };

  const handleConfirm = async () => {
    // ... [Implementation remains the same]
    if (!propertyPostId) {
      toast.error("Property Post ID is missing!");
      return;
    }
    if (!selectedSlug) {
      toast.error("No template slug selected!");
      return;
    }
    setIsProcessing(true);
    setModalOpen(true);
    try {
      const payload = {
        template_type: selectedSlug,
        finalize: false,
      };
      const res = await PropertyApi.updatePropertyPost(propertyPostId, payload);
      if (res?.error) {
        toast.error(res.error || "Failed to confirm the post stage.");
        return;
      }
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("step", CommonEnums.propertyPost.post_configuration.key);
        return next;
      });
    } catch (err) {
      console.error("Confirm error:", err);
      toast.error("Something went wrong while confirming!");
    } finally {
      setIsProcessing(false);
      setModalOpen(false);
    }
  };

  const STATIC_STAGE_TYPES = [
    "auction",
    "sold",
    "for_sale",
    "open_home",
    "coming_soon",
  ];

  const handleViewPostConfig = async (template: TemplateItem) => {
    try {
      setIsProcessing(true);

      if (
        propertyPostId &&
        !STATIC_STAGE_TYPES.includes(template.template_type)
      ) {
        await PropertyApi.updatePropertyPost(propertyPostId, {
          template_type: template.template_type,
        });
      }

      setSelectedSlug(template.slug);
      setStageVisible(false);

      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);

        next.set("stage_label", template.label);
        next.set("step", CommonEnums.propertyPost.post_configuration.key);

        if (!STATIC_STAGE_TYPES.includes(template.template_type)) {
          next.set("template_type", template.template_type);
          next.set("selected_slug", template.slug);
        } else {
          next.delete("template_type");
          next.delete("selected_slug");
        }

        return next;
      });
    } catch (err) {
      console.error(err);
      toast.error("Unable to open post configuration.");
    } finally {
      setIsProcessing(false);
    }
  };

  const getPreviewContainerClass = (postType: string | null) => {
    switch (postType) {
      case CommonEnums.postType.story_post.key:
        return "story-post-preview";
      case CommonEnums.postType.video_post.key:
        return "video-post-preview";
      case CommonEnums.postType.reel_post.key:
        return "reel-post-preview";
      default:
        return "image-post-preview";
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const toggleSkip = (template: TemplateItem, isCurrentlySkipped: boolean) => {
    if (isCurrentlySkipped) {
      setSkippedStages((prev) => prev.filter((s) => s.slug !== template.slug));
    } else {
      setSkippedStages((prev) =>
        prev.some((s) => s.slug === template.slug) ? prev : [...prev, template],
      );
    }
  };

  useEffect(() => {
    const step = searchParams.get("step");
    const templateType = searchParams.get("template_type");
    const slug = searchParams.get("selected_slug");

    if (step === "post_configuration" && templateType && slug) {
      setStageVisible(false);
      setSelectedSlug(slug);
    } else {
      setStageVisible(true);
    }
  }, [searchParams]);

  const handleToggleAccordion = (slug: string) => {
    if (expandedStage === slug) {
      setExpandedStage(null);
    } else {
      setExpandedStage(slug);
    }
  };

  const renderSocialIcons = (socials: string[] = []) => {
    const isActive = (key: string) => socials?.includes(key);
    return (
      <>
        <div className={`social-icon ${isActive("facebook") ? "active" : ""}`}>
          <FaceBookTrueIcon />
        </div>
        <div className={`social-icon ${isActive("instagram") ? "active" : ""}`}>
          <InstagramTrueIcon />
        </div>
        <div className={`social-icon ${isActive("linkedin") ? "active" : ""}`}>
          <LinkedInIcon />
        </div>
        <div className={`social-icon ${isActive("youtube") ? "active" : ""}`}>
          <YouTubeIcon />
        </div>
        <div className={`social-icon ${isActive("tiktok") ? "active" : ""}`}>
          <TikTokIcon />
        </div>
      </>
    );
  };

  const renderActivityItem = (item: any) => (
    <div key={item.id} className="activity-card-wrapper">
      <div className="activity-card">
        <div
          className={`activity-image-section ${item.orientation === "portrait" ? "story-post-image-container" : ""}`}
        >
          <img src={item.image} alt="Design" className="activity-thumbnail" />
        </div>

        <div className="activity-content-section">
          <div className="activity-social-icons-wrapper">
            <div className="social-block">
              <p className="social-title">Agent Social</p>
              <div className="activity-social-icons">
                {renderSocialIcons(item.agent_socials)}
              </div>
            </div>
            <div className="social-block">
              <p className="social-title">Agency Social</p>
              <div className="activity-social-icons">
                {renderSocialIcons(item.agency_socials)}
              </div>
            </div>
          </div>

          <div className="activity-details">
            <div className="detail-row">
              <div>
                <div className="label">Scheduled On</div>
                <div className="value">{item.scheduled_on}</div>
              </div>
              <div>
                <div className="label">Current Status</div>
                <div className="value">{item.current_status}</div>
              </div>
              <div>
                <div className="label">Posted On</div>
                <div className="value">{item.posted_on}</div>
              </div>
              <div>
                <div className="label">Generated On</div>
                <div className="value">{item.generated_on}</div>
              </div>
              <div>
                <div className="label">Last Updated On</div>
                <div className="value">{item.last_updated}</div>
              </div>
            </div>
          </div>

          <div className="activity-bottom-actions">
            <button className="btn-preview">
              <PreviewCircleIcon /> Preview
            </button>
            <button className="btn-download">
              <DownloadIcon /> Download
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStageCard = (template: TemplateItem, index: number) => {
    const isSkipped = skippedStages.some((s) => s.slug === template.slug);

    const status = template.status?.toLowerCase();
    const isCompleted = status === "completed";
    const isScheduled = status === "scheduled";
    const isPending = !isCompleted && !isScheduled;
    const isExpanded = expandedStage === template.slug;

    if (isSkipped || isPending) {
      return (
        <div
          key={template.slug + index}
          className={`stage-card ${isSkipped ? "skipped" : ""}`}
        >
          <div className="stage-left">
            <div className="stage-icon">
              <img src="/images/image-placeholder.svg" alt="" />
            </div>
            <div className="stage-info">
              <h4>{template.label}</h4>
              <p>
                {isSkipped
                  ? "This stage was skipped"
                  : "Design generation is pending"}
              </p>
            </div>
          </div>
          <div className="stage-actions">
            <div className="skip-toggle">
              <span>Skip this stage</span>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={isSkipped}
                  onChange={() => toggleSkip(template, isSkipped)}
                />
                <span className="slider round"></span>
              </label>
            </div>
            <button
              className="create-post-btn"
              disabled={isSkipped}
              onClick={() => {
                if (template.isStatic) return;
                handleSelect(template);
              }}
            >
              Create post
            </button>
          </div>
        </div>
      );
    }

    return (
      <div
        key={template.slug + index}
        className={`stage-card completed-accordion ${isExpanded ? "expanded" : ""}`}
      >
        <div
          className="accordion-header"
          onClick={() => handleToggleAccordion(template.slug)}
        >
          <div className="header-left">
            <div
              className={`status-icon-box ${isCompleted ? "success" : "neutral"}`}
            >
              {isCompleted ? (
                <img
                  src="/images/True-icon-with-square.svg"
                  alt="Success Icon"
                />
              ) : (
                <img src="/images/timeicon.svg" alt="clock" />
              )}
            </div>
            <div className="header-info">
              <h4>{template.label}</h4>
              <p>
                {isCompleted ? "Completed" : "Scheduled social media posting"}
              </p>
            </div>
          </div>

          <div className={`header-chevron ${isExpanded ? "rotate" : ""}`}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6 9L12 15L18 9"
                stroke="#1A1A1A"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>

        {isExpanded && (
          <div className="accordion-body">
            {mockExpandedItems.map((item, i) => (
              <div key={item.id} className="activity-card-wrapper">
                <div className="activity-card">
                  <div
                    className={`activity-image-section ${item.orientation === "portrait" ? "story-post-image-container" : ""}`}
                  >
                    <img
                      src={item.image}
                      alt="Design"
                      className="activity-thumbnail"
                    />
                  </div>

                  <div className="activity-content-section">
                    <div className="activity-social-icons-wrapper">
                      <div className="social-block">
                        <p className="social-title">Agent Social</p>
                        <div className="activity-social-icons">
                          {renderSocialIcons(item.agent_socials)}
                        </div>
                      </div>

                      <div className="social-block">
                        <p className="social-title">Agency Social</p>
                        <div className="activity-social-icons">
                          {renderSocialIcons(item.agency_socials)}
                        </div>
                      </div>
                    </div>

                    <div className="activity-details">
                      <div className="detail-row">
                        <div>
                          <div className="label">Scheduled On</div>
                          <div className="value">{item.scheduled_on}</div>
                        </div>
                        <div>
                          <div className="label">Current Status</div>
                          <div className="value">{item.current_status}</div>
                        </div>
                        <div>
                          <div className="label">Posted On</div>
                          <div className="value">{item.posted_on}</div>
                        </div>
                        <div>
                          <div className="label">Generated On</div>
                          <div className="value">{item.generated_on}</div>
                        </div>
                        <div>
                          <div className="label">Last Updated On</div>
                          <div className="value">{item.last_updated}</div>
                        </div>
                      </div>
                    </div>

                    <div className="activity-bottom-actions">
                      <button className="btn-preview">
                        <PreviewCircleIcon /> Preview
                      </button>
                      <button className="btn-download">
                        <DownloadIcon /> Download
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <div className="accordion-footer-actions">
              <button
                className="regenerate-btn"
                onClick={() => setViewAllModalOpen(true)}
              >
                View activity
              </button>
              <button
                className="view-all-btn"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleViewPostConfig(template);
                }}
              >
                View post config
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const activeList = allCombinedStages.filter(
    (stage) => !skippedStages.some((s) => s.slug === stage.slug),
  );

  const skippedList = allCombinedStages.filter((stage) =>
    skippedStages.some((s) => s.slug === stage.slug),
  );

  return (
    <div className="property-stage-container">
      <div className="manage-invites-list-heading">
        <div className="info-propertie-view-back-btn" onClick={handleBack}>
          <p className="bakc-text-contain">Property address</p>
        </div>
        <p className="page-title">{propertyAddress}</p>
      </div>

      {stageVisible && (
        <>
          <h2 className="stage-title">Property marketing journey</h2>

          {loading ? (
            <div className="loading-wrapper">
              <Loading />
            </div>
          ) : (
            <div className="property-marketing-journey">
              {activeList.map((template, idx) =>
                renderStageCard(template, idx),
              )}
            </div>
          )}

          <div className="skipped-stages">
            <h4>Skipped stages</h4>

            {skippedList.length === 0 ? (
              <div className="empty-skipped">
                <p>No Skipped Stages</p>
                <span>Your skipped history is currently empty</span>
              </div>
            ) : (
              skippedList.map((template, idx) => renderStageCard(template, idx))
            )}
          </div>
        </>
      )}

      <Modal
        open={viewAllModalOpen}
        onClose={() => setViewAllModalOpen(false)}
        className="view-all-modal"
      >
        <div className="property-info-view-all-modal-wrapper">
          <div className="property-info-modal-header">
            <h3>All Updates</h3>
            <button
              className="close-btn"
              onClick={() => setViewAllModalOpen(false)}
            >
              Close
            </button>
          </div>

          <div className="stage-status-bar">
            <div className="status-icon">
              <img src="/images/True-icon-with-square.svg" alt="Success" />
            </div>
            <div className="status-info">
              <h4>For Sale</h4>
              <p>Completed</p>
            </div>
          </div>

          <div className="property-info-modal-scroll-body">
            {mockExpandedItems.map((item) => renderActivityItem(item))}
          </div>

          <div className="property-info-modal-footer">
            <span>
              Viewing 1-{mockExpandedItems.length} of {mockExpandedItems.length}{" "}
              results
            </span>
            <div className="pagination-arrows">
              <button disabled>
                <LeftArrowIcon />
              </button>
              <button>
                <RightArrowIcon />
              </button>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        open={modalOpen}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            setModalOpen(false);
          }
        }}
        aria-labelledby="processing-modal"
        className="add-property-modal styled-modal"
        disableEscapeKeyDown
      >
        <div className="modal-center-wrapper">
          <div className="loading-content-wrapper">
            <div className="loading-container">
              <div className="spinner" />
              <p className="loading-text">
                Processing your request
                <span className="desktop-break">
                  <br />
                </span>
                Please wait...
              </p>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PropertyJourneyInfo;
